/**
 * Shared-midline merge for apron perimeter loops.
 *
 * Each placeable zone is wrapped in its own `apron` perimeter loop. Where two
 * *different* zones' loops face each other within `apronMergeFt`, their facing
 * nodes should collapse onto one shared midline lane (the gap centerline) so the
 * two areas feed a single lane instead of two parallel ones.
 *
 * The subtle invariant: weld only **mutual** nearest cross-zone pairs (A's
 * nearest is B and B's is A). Mutual pairs are disjoint, so two facing rows
 * collapse to a *row* of midline nodes — never a single hub. Unioning every
 * neighbor within range instead chains the whole dense apron into one centroid
 * (the prior bug). A zone never merges with its own loop, regardless of threshold.
 */
import type { TaxiNode } from './taxiGraph'

type Pt = [number, number]
const dist = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1])

export interface ApronMergeResult {
  /** Maps every input apron node id → its representative node (self or shared rep). */
  repById: Map<string, TaxiNode>
  /** Nodes to add to the plan: cluster singletons plus one shared rep per merged cluster. */
  emitted: TaxiNode[]
}

/**
 * Union mutual nearest cross-zone apron nodes within `apronMergeFt`, collapsing
 * each multi-zone cluster to a shared node at its centroid. `mkSharedNode` builds
 * that node (so node shape/flags stay owned by the caller).
 */
export function mergeApronMidlines(
  apronNodes: TaxiNode[],
  apronMergeFt: number,
  mkSharedNode: (pos: Pt, zones: string[]) => TaxiNode
): ApronMergeResult {
  const parent = new Map<string, string>()
  for (const n of apronNodes) parent.set(n.id, n.id)
  const find = (x: string): string => {
    let r = x
    while (parent.get(r) !== r) r = parent.get(r)!
    while (parent.get(x) !== r) {
      const nx = parent.get(x)!
      parent.set(x, r)
      x = nx
    }
    return r
  }
  const union = (a: string, b: string) => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent.set(ra, rb)
  }
  const zoneOf = (n: TaxiNode) => n.flags.zone as string | undefined

  if (apronMergeFt > 0 && apronNodes.length > 1) {
    const cell = apronMergeFt
    const bucket = new Map<string, TaxiNode[]>()
    for (const n of apronNodes) {
      const k = `${Math.floor(n.pos[0] / cell)}|${Math.floor(n.pos[1] / cell)}`
      const arr = bucket.get(k)
      if (arr) arr.push(n)
      else bucket.set(k, [n])
    }
    // Nearest *cross-zone* neighbor within apronMergeFt for each node.
    const nearestCross = (n: TaxiNode): string | null => {
      const cx = Math.floor(n.pos[0] / cell)
      const cz = Math.floor(n.pos[1] / cell)
      let bestId: string | null = null
      let bestD = Infinity
      for (let dx = -1; dx <= 1; dx++) {
        for (let dz = -1; dz <= 1; dz++) {
          const arr = bucket.get(`${cx + dx}|${cz + dz}`)
          if (!arr) continue
          for (const m of arr) {
            if (m.id === n.id || zoneOf(m) === zoneOf(n)) continue // cross-zone only
            const d = dist(n.pos, m.pos)
            if (d <= apronMergeFt && d < bestD) {
              bestD = d
              bestId = m.id
            }
          }
        }
      }
      return bestId
    }
    const nn = new Map<string, string>()
    for (const n of apronNodes) {
      const c = nearestCross(n)
      if (c) nn.set(n.id, c)
    }
    for (const [aId, bId] of nn) {
      if (nn.get(bId) === aId) union(aId, bId)
    }
  }

  // Collapse clusters → representatives; build old→rep map, emit merged nodes.
  const clusters = new Map<string, TaxiNode[]>()
  for (const n of apronNodes) {
    const r = find(n.id)
    const arr = clusters.get(r)
    if (arr) arr.push(n)
    else clusters.set(r, [n])
  }
  const repById = new Map<string, TaxiNode>()
  const emitted: TaxiNode[] = []
  for (const [, cluster] of clusters) {
    const zones = new Set(cluster.map(zoneOf))
    if (cluster.length === 1 || zones.size < 2) {
      for (const m of cluster) {
        repById.set(m.id, m)
        emitted.push(m)
      }
    } else {
      const cxs = cluster.reduce((s, m) => s + m.pos[0], 0) / cluster.length
      const czs = cluster.reduce((s, m) => s + m.pos[1], 0) / cluster.length
      const rep = mkSharedNode([cxs, czs], [...zones].filter(Boolean) as string[])
      for (const m of cluster) repById.set(m.id, rep)
      emitted.push(rep)
    }
  }
  return { repById, emitted }
}
